// iter135 — Slide-out drawer (uses shadcn Sheet underneath). Hosts the full
// menu grouped by role. Logout lives at the bottom. Tap any item to close.
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Link, useNavigate } from "react-router-dom";
import {
  Home, ClipboardList, FileBarChart, Settings, User, LogOut, Mail,
  LayoutGrid, Send, Megaphone, Sliders, Database, Receipt, Calculator,
  Camera, ShoppingBag, ChevronRight, FileText,
} from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { PlanMiniChip } from "@/components/plan/PlanWidgets";

const SCHOOL_GROUPS = [
  {
    label: "Workspace",
    items: [
      { to: "/dashboard",   label: "Dashboard",        Icon: Home },
      { to: "/",            label: "Quarterly UC",     Icon: FileText },
      { to: "/daily",       label: "Daily Entry",      Icon: ClipboardList },
      { to: "/consumption", label: "Daily Consumption",Icon: Calculator },
      { to: "/mdcf",        label: "Monthly MDCF",     Icon: Receipt },
      { to: "/funds",       label: "Fund Management",  Icon: ShoppingBag },
      { to: "/reports",     label: "Reports Hub",      Icon: FileBarChart },
    ],
  },
  {
    label: "Account",
    items: [
      { to: "/setup",       label: "Setup",            Icon: Settings },
      { to: "/inbox",       label: "Inbox",            Icon: Mail },
      { to: "/photo-bills", label: "Photo Bills",      Icon: Camera },
      { to: "/my-plan",     label: "My Plan",          Icon: User },
    ],
  },
];

const ADMIN_GROUPS = [
  {
    label: "Admin",
    items: [
      { to: "/admin",                  label: "Admin Dashboard", Icon: LayoutGrid },
      { to: "/inbox",                  label: "Inbox",           Icon: Send },
      { to: "/admin/field-config",     label: "Field Config",    Icon: Sliders },
      { to: "/admin/nudges",           label: "WhatsApp Nudges", Icon: Megaphone },
      { to: "/admin/migration",        label: "DB Migration",    Icon: Database },
    ],
  },
];

export default function SlideOutDrawer({ open, onClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;
  const groups = user.role === "admin" ? ADMIN_GROUPS : SCHOOL_GROUPS;
  const handleClick = () => onClose && onClose();
  const handleLogout = () => {
    onClose && onClose();
    logout && logout();
    navigate("/login");
  };
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <SheetContent
        side="left"
        data-testid="slide-out-drawer"
        className="z-[100] w-[85%] max-w-sm border-r border-slate-200 bg-white p-0"
      >
        {/* Header strip */}
        <div className="flex items-center gap-3 border-b border-slate-100 bg-gradient-to-br from-brand-600 via-brand-600 to-brand-700 px-5 py-5 text-white">
          <img src="/logo192.png" alt="" className="h-11 w-11 rounded-xl bg-white/10 p-1 ring-1 ring-white/40" />
          <div className="flex flex-col leading-tight">
            <span className="text-base font-bold">MDM Sahayak</span>
            <span className="text-xs text-white/80">{user.schoolName || user.mobile}</span>
            <span className="mt-1 inline-flex w-fit items-center gap-1 rounded-full bg-white/15 px-2 py-px text-[10px] font-bold uppercase tracking-wider">
              {user.role === "admin" ? "Admin" : "School"}
            </span>
            {user.role === "school" && <PlanMiniChip testId="drawer-plan-chip" className="mt-1 bg-white" />}
          </div>
        </div>

        {/* Menu */}
        <nav className="flex-1 overflow-y-auto p-2">
          {groups.map((group) => (
            <div key={group.label} className="px-2 pt-3">
              <p className="px-2 pb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">{group.label}</p>
              <div className="flex flex-col">
                {group.items.map(({ to, label, Icon }) => (
                  <Link
                    key={to + label}
                    to={to}
                    onClick={handleClick}
                    data-testid={`drawer-menu-item-${to.replace(/[/:]/g, "_") || "home"}`}
                    className="flex items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-brand-50 hover:text-brand-700"
                  >
                    <span className="flex items-center gap-3">
                      <Icon className="h-4 w-4 text-slate-500" />
                      {label}
                    </span>
                    <ChevronRight className="h-4 w-4 text-slate-300" />
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer / Logout */}
        <div className="border-t border-slate-100 p-3">
          <button
            type="button"
            onClick={handleLogout}
            data-testid="drawer-logout-btn"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-red-50 px-3 py-2.5 text-sm font-bold text-red-700 transition-colors hover:bg-red-100 active:scale-[0.98]"
          >
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
